import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Phone, Mail, Send, CheckCircle, AlertCircle } from 'lucide-react';
import SEO from '../../components/SEO/SEO';
import PageHeader from '../../components/PageHeader/PageHeader';
import Button from '../../components/Button/Button';
import styles from './Contact.module.css';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const isFormValid = formData.name.trim() !== '' && formData.email.trim() !== '' && formData.message.trim() !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    const fullMessage = `
Empresa: ${formData.company || 'No especificada'}
Teléfono: ${formData.phone || 'No especificado'}
Servicio de interés: ${formData.service || 'No especificado'}

Mensaje:
${formData.message}
    `.trim();

    try {
      const response = await fetch('https://cinbiosis.com/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cookie': 'crumb=BdbOq-PQXsR7NzM3M2E5MWU2MjZhMWE0NWE4ZDI5YjhhMWZkYjMx'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: fullMessage
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', company: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.contactPage}>
      <SEO
        title={t('seo.contactTitle')}
        description={t('seo.contactDesc')}
        canonicalPath={`/${isEn ? 'contact' : 'contacto'}`}
      />

      <PageHeader
        title={t('contactPage.headerTitle')}
        description={t('contactPage.headerDesc')}
      />

      <section className={styles.contactSection}>
        <div className="container">
          <div className={styles.contactLayout}>
            {/* Info Side */}
            <div className={styles.contactInfo}>
              <h2>{t('contactPage.infoTitle')}</h2>

              <div className={styles.infoCard}>
                <div className={styles.iconWrapper}><MapPin size={24} /></div>
                <div>
                  <h4>{t('contactPage.addressTitle')}</h4>
                  <p>{t('contactPage.addressText')}</p>
                </div>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.iconWrapper}><Phone size={24} /></div>
                <div>
                  <h4>{t('contactPage.phoneTitle')}</h4>
                  <p>{t('contactPage.phoneText')}</p>
                </div>
              </div>

              <div className={styles.infoCard}>
                <div className={styles.iconWrapper}><Mail size={24} /></div>
                <div>
                  <h4>{t('contactPage.emailTitle')}</h4>
                  <p>{t('contactPage.emailText')}</p>
                </div>
              </div>
            </div>

            {/* Form Side */}
            <div className={styles.contactFormWrapper}>
              <h2>{t('contactPage.formTitle')}</h2>
              <form className={styles.contactForm} onSubmit={handleSubmit}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">{t('contactPage.formName')}</label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={styles.formControl} required />
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="company">{t('contactPage.formCompany')}</label>
                  <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={styles.formControl} />
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label htmlFor="email">{t('contactPage.formEmail')}</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={styles.formControl} required />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="phone">{t('contactPage.formPhone')}</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={styles.formControl} />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="service">{t('contactPage.formService')}</label>
                  <select id="service" name="service" value={formData.service} onChange={handleChange} className={styles.formControl}>
                    <option value="">{t('contactPage.servicesOptions.default')}</option>
                    <option value="biotic">{t('contactPage.servicesOptions.biotic')}</option>
                    <option value="licensing">{t('contactPage.servicesOptions.licensing')}</option>
                    <option value="climate">{t('contactPage.servicesOptions.climate')}</option>
                    <option value="geospatial">{t('contactPage.servicesOptions.geospatial')}</option>
                    <option value="other">{t('contactPage.servicesOptions.other')}</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="message">{t('contactPage.formMessage')}</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows="5" className={styles.formControl} required></textarea>
                </div>

                {submitStatus === 'success' && (
                  <div style={{ backgroundColor: '#e6f4ea', color: '#137333', padding: '12px', borderRadius: '4px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                    <CheckCircle size={20} style={{ marginRight: '8px' }} />
                    {t('contactPage.successMessage')}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div style={{ backgroundColor: '#fce8e6', color: '#c5221f', padding: '12px', borderRadius: '4px', marginBottom: '16px', display: 'flex', alignItems: 'center' }}>
                    <AlertCircle size={20} style={{ marginRight: '8px' }} />
                    {t('contactPage.errorMessage')}
                  </div>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  disabled={!isFormValid || isSubmitting}
                  style={{ width: '100%', justifyContent: 'center', opacity: (!isFormValid || isSubmitting) ? 0.6 : 1, cursor: (!isFormValid || isSubmitting) ? 'not-allowed' : 'pointer' }}
                >
                  <Send size={18} style={{ marginRight: '8px' }} />
                  {isSubmitting ? 'Enviando...' : t('contactPage.formSubmit')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
